import React, { useState, useEffect } from 'react';
import { Flag, Plus, List, X, Trash2 } from 'lucide-react';
import { COLORS, GOAL_TEMPLATES, CATEGORIES } from '../data/mockData';
import { fetchGoals, createGoal, deleteGoal as apiDeleteGoal } from '../services/api';
import GoalsRadar from '../components/GoalsRadar';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';

export default function GoalsPage() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [newSeverity, setNewSeverity] = useState(3);

  useEffect(() => {
    fetchGoals().then(data => { setGoals(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const addGoal = async () => {
    if (!newTitle.trim()) return;
    try {
      const goal = await createGoal({ title: newTitle, description: newDesc, category: newCategory, severity: newSeverity });
      setGoals([goal, ...goals]);
      setShowAdd(false);
      setNewTitle(''); setNewDesc(''); setNewCategory('general'); setNewSeverity(3);
    } catch (err) {
      console.error('Failed to create goal', err);
    }
  };

  const handleDeleteGoal = async (id) => {
    try {
      await apiDeleteGoal(id);
      setGoals(goals.filter(g => g.goal_id !== id));
    } catch (err) {
      console.error('Failed to delete goal', err);
    }
  };

  const applyTemplate = (t) => {
    setNewTitle(t.title); setNewDesc(t.description); setNewCategory(t.category);
    setShowTemplates(false); setShowAdd(true);
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: COLORS.textSecondary }}>Loading goals...</div>;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 32px' }}>
      {/* INTRO VIDEO (members-only, 60% size) */}
      <div data-testid="goals-intro-video-wrap" style={{ marginBottom: 16 }}>
        <VideoWithSoundToggle
          src="/media/goals-intro.mp4"
          testId="goals-intro-video"
          aspectRatio="16 / 9"
          maxWidth={504}
          objectFit="cover"
        />
      </div>

      <GoalsRadar />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Health Goals</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowTemplates(true)} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
            <List size={20} color={COLORS.primary} />
          </button>
          <button onClick={() => setShowAdd(true)} style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' }}>
            <Plus size={22} color="#FFF" />
          </button>
        </div>
      </div>

      {goals.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}>
          <Flag size={48} color={COLORS.border} />
          <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginTop: 12, fontFamily: 'Outfit, sans-serif' }}>No Goals Yet</h2>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, maxWidth: 260, margin: '8px auto 16px' }}>Start by adding a health goal or choosing from templates</p>
          <button onClick={() => setShowAdd(true)} style={{ backgroundColor: COLORS.primary, borderRadius: 9999, padding: '12px 24px', border: 'none', cursor: 'pointer' }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#FFF' }}>Create Your First Goal</span>
          </button>
        </div>
      ) : (
        goals.map((goal) => (
          <div key={goal.goal_id} style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }} className="goal-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: goal.severity <= 2 ? COLORS.success : goal.severity <= 4 ? COLORS.accentWarm : COLORS.error }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>{goal.title}</div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, textTransform: 'capitalize', marginTop: 2 }}>{(goal.category || '').replace('_', ' ')}</div>
              </div>
              <button onClick={() => handleDeleteGoal(goal.goal_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Trash2 size={18} color={COLORS.textSecondary} />
              </button>
            </div>
            {goal.description && <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.4, marginBottom: 8, margin: 0 }}>{goal.description}</p>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <span style={{ backgroundColor: COLORS.surfaceSecondary, padding: '4px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600, color: COLORS.textSecondary }}>Severity: {goal.severity}/7</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.primary, textTransform: 'capitalize' }}>{goal.status}</span>
            </div>
          </div>
        ))
      )}

      {/* Add Goal Modal */}
      {showAdd && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: COLORS.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>New Goal</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} color={COLORS.textPrimary} /></button>
            </div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block' }}>Title</label>
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Improve cardio endurance" style={{ width: '100%', backgroundColor: COLORS.background, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: '0 16px', height: 52, fontSize: 16, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Work Sans, sans-serif' }} />
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block' }}>Description</label>
            <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Describe your goal..." rows={3} style={{ width: '100%', backgroundColor: COLORS.background, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 16, fontSize: 16, color: COLORS.textPrimary, marginBottom: 16, resize: 'none', fontFamily: 'Work Sans, sans-serif' }} />
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block' }}>Category</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setNewCategory(c)} style={{ padding: '8px 14px', borderRadius: 9999, backgroundColor: newCategory === c ? COLORS.primary : COLORS.background, border: `1px solid ${newCategory === c ? COLORS.primary : COLORS.border}`, color: newCategory === c ? '#FFF' : COLORS.textSecondary, fontSize: 13, cursor: 'pointer', fontWeight: newCategory === c ? 600 : 400, textTransform: 'capitalize' }}>
                  {c.replace('_', ' ')}
                </button>
              ))}
            </div>
            <label style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 6, display: 'block' }}>Severity (1-7)</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[1, 2, 3, 4, 5, 6, 7].map(n => (
                <button key={n} onClick={() => setNewSeverity(n)} style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: newSeverity === n ? COLORS.primary : COLORS.background, border: `1px solid ${newSeverity === n ? COLORS.primary : COLORS.border}`, color: newSeverity === n ? '#FFF' : COLORS.textPrimary, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                  {n}
                </button>
              ))}
            </div>
            <button onClick={addGoal} style={{ width: '100%', backgroundColor: COLORS.primary, borderRadius: 9999, padding: 16, border: 'none', cursor: 'pointer' }}>
              <span style={{ fontSize: 17, fontWeight: 700, color: '#FFF' }}>Save Goal</span>
            </button>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ backgroundColor: COLORS.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif', margin: 0 }}>Goal Templates</h2>
              <button onClick={() => setShowTemplates(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} color={COLORS.textPrimary} /></button>
            </div>
            {GOAL_TEMPLATES.map((t, i) => (
              <button key={i} onClick={() => applyTemplate(t)} style={{ display: 'block', width: '100%', textAlign: 'left', backgroundColor: COLORS.background, borderRadius: 8, padding: 16, marginBottom: 12, border: 'none', cursor: 'pointer' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 4 }}>{t.title}</div>
                <div style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.4, marginBottom: 8 }}>{t.description}</div>
                <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600, textTransform: 'capitalize' }}>{(t.category || '').replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
